import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import * as XLSX from 'xlsx';
import { TableColumn } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/table-column';

export type ExportResult = 'done' | 'cancelled';

@Injectable({
  providedIn: 'root',
})
export class ExcelExportService {
  exportToExcel(data: any[], columns: TableColumn[], fileName: string = 'export'): void {
    const exportColumns = columns.filter((col) => col.key !== 'actions');
    const headers = exportColumns.map((col) => col.title || col.key);
    const exportData = this.prepareRows(data, exportColumns, headers);

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
    worksheet['!cols'] = headers.map((header) => ({ wch: Math.max(header.length + 2, 15) }));

    const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, fileName);
  }

  exportWithWorker(
    data: any[],
    columns: TableColumn[],
    selectedKeys: string[],
    fileName: string,
    progress$: Subject<number>,
    cancel$: Subject<void>,
  ): Observable<ExportResult> {
    const exportColumns = columns.filter((c) => selectedKeys.includes(c.key));
    const headers = exportColumns.map((c) => c.title || c.key);
    const rows = this.prepareRows(data, exportColumns, headers);
    return this.runWorker(rows, headers, fileName, progress$, cancel$);
  }

  /**
   * Exporta filas ya construidas (cabeceras y celdas resueltas por el llamante) a Excel
   * mediante el web worker. Útil cuando las columnas son dinámicas y no derivan de un
   * `TableColumn[]` de tabla (ej. exportación de respuestas de formularios — JRS-036).
   *
   * @param rows - Filas ya preparadas, cada una un objeto cuyas claves son las cabeceras.
   * @param headers - Cabeceras en orden (se usan para el ancho de columnas del worker).
   */
  exportRowsWithWorker(
    rows: Record<string, unknown>[],
    headers: string[],
    fileName: string,
    progress$: Subject<number>,
    cancel$: Subject<void>,
  ): Observable<ExportResult> {
    return this.runWorker(rows, headers, fileName, progress$, cancel$);
  }

  /**
   * Cableado común del web worker: progreso, descarga al completar, cancelación y errores.
   */
  private runWorker(
    rows: Record<string, unknown>[],
    headers: string[],
    fileName: string,
    progress$: Subject<number>,
    cancel$: Subject<void>,
  ): Observable<ExportResult> {
    return new Observable<ExportResult>((observer) => {
      const worker = new Worker(new URL('./excel-export.worker', import.meta.url), {
        type: 'module',
      });

      const cancelSub = cancel$.subscribe(() => {
        worker.terminate();
        observer.next('cancelled');
        observer.complete();
      });

      worker.onmessage = ({ data: msg }) => {
        if (msg.type === 'progress') {
          progress$.next(msg.percent);
        } else if (msg.type === 'done') {
          this.saveAsExcelFile(msg.buffer, fileName);
          worker.terminate();
          cancelSub.unsubscribe();
          observer.next('done');
          observer.complete();
        } else if (msg.type === 'error') {
          console.error('[ExcelExportWorker] worker error:', msg.message);
          worker.terminate();
          cancelSub.unsubscribe();
          observer.error(msg.message);
        }
      };

      worker.onerror = (e) => {
        console.error('[ExcelExportWorker] onerror:', e);
        worker.terminate();
        cancelSub.unsubscribe();
        observer.error(e.message);
      };

      worker.postMessage({ rows, headers, fileName });

      return () => {
        cancelSub.unsubscribe();
        worker.terminate();
      };
    });
  }

  private prepareRows(
    data: any[],
    columns: TableColumn[],
    headers: string[],
  ): Record<string, unknown>[] {
    return data.map((row) => {
      const rowData: Record<string, unknown> = {};
      columns.forEach((col, i) => {
        let value = row[col.key];
        if (col.mapping && value !== undefined && value !== null) {
          value = col.mapping[value] || value;
        }
        if (col.type === 'date' && value) {
          value = new Date(value).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
        }
        rowData[headers[i]] = value ?? '';
      });
      return rowData;
    });
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(data);
    link.download = `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
  }
}
